import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { notification } from 'src/components/Notification';
import { useCapUpsert } from 'src/hooks/cap';
import { READY_FOR_CAP } from 'src/utils/data/job';
import { shouldDisableSubmit, shouldDisableSave  } from '../utils';
import s from './CapBoxPageHeader.styles';

export const CapBoxPageHeader = ({ 
  values,
  setLoading, 
  reloadCapFindings, 
  jobId, 
  setIsDirty, 
  isEditable, 
  handleSubmit, 
  hasError, 
  isCapManager, 
  errors, 
  isExternalUser, 
  originalFindings, 
  overallCapStatusValue, 
  onExportClicked, 
  isDirty, 
  isAdmin, 
  wasCapSavedBefore,
  jobStatus
}) => {
  const { t } = useTranslation();
  const upsertCap = useCapUpsert();
  const [hasErrorBoundary, setHasErrorBoundary] = useState(hasError);
  const [disableSubmitButton, setDisableSubmitButton] = useState(true);
  const [errorBoundaryDetails, setErrorBoundaryDetails] = useState({
    title: t('REQUIRED_HEADER'),
    content: t('ERROR_FILL_MANDATORY_FIELDS_CONTENT')
  });
  const [actionsNotChangedErrorDetails, setActionsNotChangedErrorDetails] = useState([]);
  const [showActionErrors, setShowActionErrors] = useState([]);
  const [actionChanged, setActionChanged] = useState(false);

  const disableSave = shouldDisableSave(
    isExternalUser,
    isAdmin,
    isCapManager,
    isDirty,
    overallCapStatusValue
  );

  const showExportButton = jobStatus !== READY_FOR_CAP;

  useEffect(() => {
    const shouldDisable = shouldDisableSubmit(
      isExternalUser,
      isAdmin,
      isCapManager,
      overallCapStatusValue,
      wasCapSavedBefore,
    );

    setDisableSubmitButton(shouldDisable);
  }, [isExternalUser]);

  useEffect(() => {
    if (isDirty) {
      setDisableSubmitButton(false);
    }
  }, [isDirty]);
  
  useEffect(() => {
    setHasErrorBoundary(hasError);
  }, [hasError]);

  const compareOriginalCurrentAction = (
    originalData,
    currentData
  ) => {
    const notChangedAction = [];
    const originarlFindingsArr = originalData;
    const currentFindingsArr = currentData?.findings;
    if (isExternalUser) {
      currentFindingsArr.map((findingItem, fIndex) => {
        const currentActions = findingItem.actions;
        const originalActions = originarlFindingsArr?.filter((item) => item.id === findingItem.id)[0]?.actions;
        currentActions.map((currentActionItem, aIndex) => {
          const originalActionItem = originalActions?.filter((actionItem) => currentActionItem.id === actionItem.id)[0];
          if ((currentActionItem?.status === 'open_in_process' || currentActionItem?.status === 'open_action_pending' || currentActionItem?.status === 'revision_requested' )
          && new Date(originalActionItem?.savedAt) < new Date(originalActionItem?.submittedAt)
          && (currentActionItem?.dueDate === originalActionItem?.dueDate
            && currentActionItem?.actualCompletionOfActionDate === originalActionItem?.actualCompletionOfActionDate
            && currentActionItem?.description === originalActionItem?.description
            && currentActionItem?.responsiblePerson === originalActionItem?.responsiblePerson
            && currentActionItem?.comments.length === originalActionItem?.comments.length
            && JSON.stringify(currentActionItem?.attachmentIds) === JSON.stringify(originalActionItem?.attachmentIds))) {
            const currentResources = currentActionItem.resources;
            const originalResources = originalActionItem.resources;
            notChangedAction.push({findingIndex: fIndex, actionIndex: aIndex});
            currentResources.map((resource) => {
              const originalresource = originalResources?.filter((resourceItem) => resourceItem.id === resource.id)[0];
              if (!(originalresource && resource?.averageDayRate === originalresource?.averageDayRate
                && resource?.costs === originalresource?.costs
                && resource?.costs === originalresource?.costs
                && resource?.currencyId === originalresource?.currencyId
                && resource?.details === originalresource?.details
                && resource?.numberOfStaff === originalresource?.numberOfStaff
                && resource?.staffDayType === originalresource?.staffDayType
                && resource?.staffDayValue === originalresource?.staffDayValue )) {
                  notChangedAction.pop();
                }
              return resource;
            })            
            const currentComments = currentActionItem.comments;
            const originalComments = originalActionItem.comments;
            currentComments.map((comment) => {                
              const originalComment = originalComments?.filter((commentItem) => commentItem?.comment === comment?.comment)[0];
              if(!originalComment) {
                notChangedAction.pop();
              }
              return comment;
            })
          }
          return true;
        });
      return findingItem;
      })
    }
    setActionsNotChangedErrorDetails(notChangedAction);
    return notChangedAction.length !== 0;
  };
  
  useEffect(() => {
    setActionChanged(!compareOriginalCurrentAction(originalFindings, values));
  }, [originalFindings, values]);

  useEffect(() => {
    setHasErrorBoundary(false);
    const { findings, clientId: clientIdHasError } = errors && {...errors};
    let actionResourceErrorFlag = false;
    if (clientIdHasError) {
      setErrorBoundaryDetails({
        title: t('REQUIRED_HEADER'),
        content: t('ERROR_FILL_MANDATORY_FIELDS_CONTENT')
      });
      setHasErrorBoundary(true);
      return;
    }
    for (let i = 0; i < findings?.length; i+=1) {
      const finding = findings[i];
      if (!actionResourceErrorFlag && finding && ( typeof finding.actions === 'string')) {
        setErrorBoundaryDetails({
          title: t('REQUIRED_HEADER'),
          content: t('ERROR_REQUIRED_ACTION_RESOURCE_CONTENT')
        });
        setHasErrorBoundary(true);
        actionResourceErrorFlag = true;
        break;
      } else if (!actionResourceErrorFlag) {
        if(finding?.actions.find(item => (item && typeof item.resources === 'string'))){
          setErrorBoundaryDetails({
            title: t('REQUIRED_HEADER'),
            content: t('ERROR_REQUIRED_ACTION_RESOURCE_CONTENT')
          });
          setHasErrorBoundary(true);
          actionResourceErrorFlag = true;
          break;
        } else {
        for (let j = 0; j < finding?.actions.length; j+=1) {
          const actionsError = finding?.actions[j];
          if (actionsError && !actionsError?.status && ( actionsError?.clientId !== '' || actionsError?.dueDate !== '' || actionsError?.actualCompletionOfActionDate !== '' || actionsError?.responsiblePerson !== '' || actionsError.description !== '' || actionsError?.resources)) {
            setErrorBoundaryDetails({
              title: t('REQUIRED_HEADER'),
              content: t('ERROR_FILL_MANDATORY_FIELDS_CONTENT')
            });
            setHasErrorBoundary(true);
            break;
          } else if (!actionChanged || actionsError?.status) {
            setErrorBoundaryDetails({
              title: t('REQUIRED_HEADER'),
              content: t('ERROR_UPDATE_CAP_BEFORE_SUBMITTING')
            });
            setHasErrorBoundary(true);
            break;
          }
        }
    }}}
  }, [errors]);

  const checkActionItemErrors = () => {
    setIsDirty(false);
    setDisableSubmitButton(true);
    if (!actionChanged) {
      setHasErrorBoundary(true);
      setShowActionErrors(actionsNotChangedErrorDetails);
    } else {
      if (errors && !('findings' in errors)) {
        setShowActionErrors([]);
        setHasErrorBoundary(false);
      }
      handleSubmit();
    }
  }

  const handleCapSave = async () => {
    try {
        setLoading(true);
        const response = await upsertCap({ ...values, actionType: 'save' });
        const {
          data: {
            UpsertCap: {
              success,
              error
            },
          }
        } = response;
        if (success) {
          notification.success(t('SAVE_CAP'));
          setIsDirty(false);
          reloadCapFindings(jobId);
        } else if(!success && !error ) {
          throw new Error(t('SAVE_ERROR'));
        }
    } catch (e) {
      notification.error(e);
    } finally {
      setLoading(false);
    }
  };

  const renderErrorDetails = () => {
    return errors?.findings
    .map((finding, fIndex) => {
      if (Array.isArray(finding?.actions)) {
        return finding.actions
          .map((action, aIndex) => {
            if (action)
            return <li key={`${fIndex}-${aIndex}`}>Area of improvement {fIndex + 1} - Action Item {aIndex + 1}</li>
            return null;
          })
      }
      return null;
    })
    .filter(Boolean)
  }

  return <s.CapHeader>
    <s.CapTitle>{t('CAP_AND_IMPLEMENTATION_PROGRESS')}</s.CapTitle>
    { isEditable && <s.CapTabActionRow>
        { showExportButton && <s.ExportButton 
          variant='btn--outline-link'
          label={t('EXPORT')}
          onClick={() => onExportClicked()}
        />}
        <s.SaveButton
          variant='btn--outline-link'
          label={t('SAVE')}
          onClick={handleCapSave}
          disabled={disableSave}
        ></s.SaveButton>
        <s.SaveButton
          variant='btn--outline-link'
          label={t('SUBMIT')}
          disabled={disableSubmitButton}
          onClick={() => {
            checkActionItemErrors();
          }}
        ></s.SaveButton>
      </s.CapTabActionRow>
    }
    { hasErrorBoundary && <s.CapTabRequired>
        <s.CapTabRequiredHeader>
          {errorBoundaryDetails ? errorBoundaryDetails.title : t('REQUIRED_HEADER')}
        </s.CapTabRequiredHeader>
        <s.CapTabRequiredContent>
          {errorBoundaryDetails ? errorBoundaryDetails.content : t('REQUIRED_CONTENT')}
        </s.CapTabRequiredContent>
        {errors?.findings?.length && <s.CapTabErrorDetails>
          <ul>{renderErrorDetails()}</ul>
        </s.CapTabErrorDetails>
        }
        {showActionErrors?.length > 0 && <s.CapTabErrorDetails>
          <ul>
            {showActionErrors.map(({findingIndex, actionIndex}) => 
              <li key={`${findingIndex}-${actionIndex}`}>
                Area of improvement {findingIndex + 1} - Action Item {actionIndex + 1}
              </li>
            )}
          </ul>
        </s.CapTabErrorDetails>
        }
      </s.CapTabRequired>
    }
    <s.IndicateRequired>{t('INDICATES_REQUIRED_FIELD')}</s.IndicateRequired>
  </s.CapHeader>
}

CapBoxPageHeader.propTypes = {
  values: PropTypes.object,
  setLoading: PropTypes.func, 
  reloadCapFindings: PropTypes.func, 
  jobId: PropTypes.number, 
  setIsDirty: PropTypes.func, 
  isEditable: PropTypes.bool, 
  handleSubmit: PropTypes.func, 
  hasError: PropTypes.bool, 
  isCapManager: PropTypes.bool, 
  errors: PropTypes.object, 
  isExternalUser: PropTypes.bool, 
  originalFindings: PropTypes.object, 
  overallCapStatusValue: PropTypes.string, 
  onExportClicked: PropTypes.func, 
  isDirty: PropTypes.bool, 
  isAdmin: PropTypes.bool, 
  wasCapSavedBefore: PropTypes.bool,
};
